import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

type ChangeFreq = 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
export type SeoIndexPolicy = 'index,follow' | 'noindex,nofollow';

export interface SeoPageConfig {
  title: string;
  description: string;
  ogImage: string; // absolute URL
  changefreq?: ChangeFreq;
  priority?: number;
}

const PROD_ORIGIN = 'https://rtk-expert.geoastra.com';
const PROD_HOSTNAME = 'rtk-expert.geoastra.com';

// Public pages allowlist: only these routes may be indexed (on production hostname only).
const PUBLIC_PAGES: Record<string, SeoPageConfig> = {
  '/': {
    title: 'GeoAstra RTK Expert — Web RTK/PPK Toolkit',
    description:
      'GeoAstra RTK Expert is a web-based RTK/PPK toolkit including PPK Studio, PPK Downloader, and RTK Base Analyzer.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_PPK_Studio_small.png`,
    changefreq: 'daily',
    priority: 0.9
  },
  '/landing-ntrip-caster': {
    title: 'GeoAstra RTK Expert — Overview',
    description:
      'Learn about GeoAstra RTK Expert and its three tools: PPK Studio, PPK Downloader, and RTK Base Analyzer.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_PPK_Studio_small.png`,
    changefreq: 'weekly',
    priority: 0.8
  },
  '/landing-quick-start': {
    title: 'Quick Start — GeoAstra RTK Expert',
    description: 'Quick start for RTK Expert workflows and getting started with the toolkit.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/ppk_expert.png`,
    changefreq: 'monthly',
    priority: 0.6
  },
  '/landing-whats-new': {
    title: 'What’s New — GeoAstra RTK Expert',
    description: 'Latest updates and improvements across PPK Studio, PPK Downloader, and RTK Base Analyzer.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/dashboard_screen.png`,
    changefreq: 'monthly',
    priority: 0.6
  },
  '/landing-ppk-downloader': {
    title: 'PPK Downloader — Download RTCM for PPK',
    description:
      'Download RTCM correction data from mountpoints for PPK workflows with scheduling and status tracking.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/ppk-downloader_screen.png`,
    changefreq: 'monthly',
    priority: 0.5
  },
  '/landing-rtk-base-analyzer': {
    title: 'RTK Base Analyzer — Analyze RTCM Streams',
    description: 'Analyze RTCM streams to gain insights about RTK base data and mountpoint performance.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/analyzer_screen.png`,
    changefreq: 'monthly',
    priority: 0.5
  },
  '/about-us': {
    title: 'About GeoAstra',
    description:
      'GeoAstra builds RTK software and RTK GNSS receivers to make centimeter-level positioning more affordable and accessible.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_logo_200x200.png`,
    changefreq: 'yearly',
    priority: 0.3
  },
  '/contact-us': {
    title: 'Contact GeoAstra',
    description: 'Contact GeoAstra for questions about RTK Expert, PPK Studio, or partnerships.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/profile.png`,
    changefreq: 'yearly',
    priority: 0.3
  },
  '/refund-policy': {
    title: 'Refund Policy — GeoAstra',
    description: 'GeoAstra refund policy for software and services.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_logo_200x200.png`,
    changefreq: 'yearly',
    priority: 0.2
  },
  '/terms-conditions': {
    title: 'Terms & Conditions — GeoAstra',
    description: 'Terms and conditions for using GeoAstra RTK Expert.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_logo_200x200.png`,
    changefreq: 'yearly',
    priority: 0.2
  },
  '/security-privacy': {
    title: 'Security & Privacy — GeoAstra',
    description: 'Security and privacy information for GeoAstra services.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_logo_200x200.png`,
    changefreq: 'yearly',
    priority: 0.2
  },
  '/data-processing': {
    title: 'Data Processing — GeoAstra',
    description: 'How GeoAstra processes and protects your data across our services.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_logo_200x200.png`,
    changefreq: 'yearly',
    priority: 0.2
  },
  '/disclaimer': {
    title: 'Disclaimer — GeoAstra',
    description: 'Disclaimer for GeoAstra websites, software, and services.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_logo_200x200.png`,
    changefreq: 'yearly',
    priority: 0.2
  },
  '/copyright': {
    title: 'Copyright — GeoAstra',
    description: 'Copyright information for GeoAstra content and materials.',
    ogImage: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_logo_200x200.png`,
    changefreq: 'yearly',
    priority: 0.2
  }
};

@Injectable({ providedIn: 'root' })
export class SeoService {
  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document
  ) {}

  normalizePath(url: string): string {
    const raw = (url || '/').split('?')[0].split('#')[0] || '/';
    const withSlash = raw.startsWith('/') ? raw : `/${raw}`;
    if (withSlash !== '/' && withSlash.endsWith('/')) return withSlash.slice(0, -1);
    return withSlash;
  }

  computeRobotsPolicy(path: string): SeoIndexPolicy {
    const hostname = (typeof window !== 'undefined' && window.location?.hostname) || '';
    const isProdHost = hostname === PROD_HOSTNAME;
    const isPublic = !!PUBLIC_PAGES[path];
    return isProdHost && isPublic ? 'index,follow' : 'noindex,nofollow';
  }

  canonicalUrlForPath(path: string): string {
    if (path === '/') return `${PROD_ORIGIN}/`;
    return `${PROD_ORIGIN}${path}`;
  }

  applyForUrl(url: string): void {
    const path = this.normalizePath(url);
    const page = PUBLIC_PAGES[path];
    const robots = this.computeRobotsPolicy(path);

    const canonical = this.canonicalUrlForPath(path);
    const title = page?.title || 'GeoAstra RTK Expert';
    const description = page?.description || 'GeoAstra RTK Expert.';
    const ogImage = page?.ogImage || `${PROD_ORIGIN}/assets/images/landing/GeoAstra_logo_200x200.png`;

    this.title.setTitle(title);

    this.meta.updateTag({ name: 'description', content: description }, 'name="description"');
    this.meta.updateTag({ name: 'robots', content: robots }, 'name="robots"');

    // OpenGraph
    this.meta.updateTag({ property: 'og:title', content: title }, 'property="og:title"');
    this.meta.updateTag({ property: 'og:description', content: description }, 'property="og:description"');
    this.meta.updateTag({ property: 'og:url', content: canonical }, 'property="og:url"');
    this.meta.updateTag({ property: 'og:type', content: 'website' }, 'property="og:type"');
    this.meta.updateTag({ property: 'og:image', content: ogImage }, 'property="og:image"');

    // Twitter
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' }, 'name="twitter:card"');
    this.meta.updateTag({ name: 'twitter:title', content: title }, 'name="twitter:title"');
    this.meta.updateTag({ name: 'twitter:description', content: description }, 'name="twitter:description"');
    this.meta.updateTag({ name: 'twitter:image', content: ogImage }, 'name="twitter:image"');

    this.upsertCanonicalLink(canonical);
    this.upsertOrganizationJsonLd();
  }

  private upsertCanonicalLink(href: string): void {
    const head = this.document.head;
    let link = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', href);
  }

  private upsertJsonLdScript(id: string, data: unknown): void {
    const head = this.document.head;
    let script = head.querySelector<HTMLScriptElement>(`script#${id}`);
    if (!script) {
      script = this.document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      head.appendChild(script);
    }
    script.text = JSON.stringify(data);
  }

  private upsertOrganizationJsonLd(): void {
    const org = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'GeoAstra',
      url: PROD_ORIGIN,
      logo: `${PROD_ORIGIN}/assets/images/landing/GeoAstra_logo_200x200.png`,
      email: 'info@geoastra.com',
      contactPoint: [
        {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: 'info@geoastra.com'
        }
      ]
    };
    this.upsertJsonLdScript('ld-org', org);
  }
}


